import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Package, Plus, Upload, Tag } from 'lucide-react';

export default function SchoolPayPerUse() {
  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Package className="w-7 h-7 text-[#1E4D35]" />
          Pay-Per-Use & Non-CDS Books
        </h1>
        <p className="text-slate-500 mt-1">Additional copies, non-CDS cataloguing, and spine label replacement</p>
      </div>

      <Tabs defaultValue="additional">
        <TabsList>
          <TabsTrigger value="additional">Additional CDS Copies</TabsTrigger>
          <TabsTrigger value="noncds">Non-CDS Cataloguing</TabsTrigger>
          <TabsTrigger value="spine">Spine Label Replacement</TabsTrigger>
          <TabsTrigger value="history">Order History</TabsTrigger>
        </TabsList>

        <TabsContent value="additional" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Additional CDS Copies</CardTitle>
              <CardDescription>Order extra copies of CDS titles post-submission. Billed as pay-per-use.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <div>
                    <p className="font-medium">Between Two Worlds ×2</p>
                    <p className="text-sm text-slate-500">Already ordered during selection</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Included in Jan 2028</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <div>
                    <p className="font-medium">Climate Change & You ×1</p>
                    <p className="text-sm text-slate-500">Already ordered during selection</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Included in Jan 2028</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                  <div>
                    <p className="font-medium">Code Red ×1</p>
                    <p className="text-sm text-slate-500">Already ordered during selection</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Included in Jan 2028</Badge>
                </div>
              </div>
              <Button variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Request Additional Copies
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="noncds" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Non-CDS Cataloguing</CardTitle>
              <CardDescription>Submit books from other sources for cataloguing + processing. Limit: Up to 100 books per school per term.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card className="bg-slate-50">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">45</div>
                    <div className="text-sm text-slate-500">Books submitted (Term 1 2028)</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">55</div>
                    <div className="text-sm text-slate-500">Quota remaining</div>
                  </CardContent>
                </Card>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Collection: Civica will collect with Jan 2028 delivery | Return: By Term 2 2028
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Harry Potter & The Sorcerer's Stone</p>
                    <p className="text-xs text-slate-500">978-0439708180 | Donated — parent</p>
                  </div>
                  <Badge variant="outline">Added</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">The Giver</p>
                    <p className="text-xs text-slate-500">978-0544336261 | Purchased — Book Depository</p>
                  </div>
                  <Badge variant="outline">Added</Badge>
                </div>
              </div>
              <p className="text-sm text-slate-500 mb-4">+ 43 more books | Estimated invoice: 45 × $12 = $540 (Tier 1)</p>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Book
                </Button>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spine" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Spine Label Replacement</CardTitle>
              <CardDescription>Request replacement labels for worn/damaged labels</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">"The Wonders of Coding"</p>
                    <p className="text-xs text-slate-500">BC-RGS-00234 | Label torn</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Requested</Badge>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">"Daughters of Singapore"</p>
                    <p className="text-xs text-slate-500">BC-RGS-00187 | Label faded</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Requested</Badge>
                </div>
              </div>
              <Button variant="outline">
                <Tag className="w-4 h-4 mr-2" />
                Add Request
              </Button>
              <p className="text-xs text-slate-500 mt-2">Dispatched with next monthly delivery</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Order History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">PPU Additional Copies — Jan 2028</p>
                    <p className="text-xs text-slate-500">4 books | INV-RGS-PPU-JAN001</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">$65.00</p>
                    <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Non-CDS Cataloguing — Term 2 2027</p>
                    <p className="text-xs text-slate-500">30 books | INV-RGS-NONCDS-T2</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">$360.00</p>
                    <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Spine Label Replacement — Term 1 2027</p>
                    <p className="text-xs text-slate-500">5 labels | INV-RGS-SPINE-T1</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">$25.00</p>
                    <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
